import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import UploadWorkspaceDocument from "./UploadWorkspaceDocument";
import "../../css/ImportDocument.css"
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"

function ImportWorkspaceDocument() {
    const [showUpload, setShowUpload] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [workspaceDetails, setWorkspaceDetails] = useState(null);
    const [uid, setUid] = useState("");
    const { customDomain } = useParams();
    const { enhancedRightClickEnabled } = useQuickLinks();
    const navigate = useNavigate();

    // Authentication Effect
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
            } else {
                navigate("/login");
            }
        });

        return () => {
            unsubscribe();
        };
    }, [navigate]);

    // Fetch workspace details
    useEffect(() => {
        if (uid && customDomain) {
            fetchWorkspaceDetails();
        }
    }, [uid, customDomain]);

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      url: "/login",
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      url: "/register",
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      url: "/my-notebook",
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      url: "https://www.youtube.com/@schoolofintology4062",
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      url: "https://discord.gg/DYkESHwgVA",
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];

const loggedInOptions = [
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => navigate("/"),
      },
  {
    label: "Your Documents",
    url: "/documentation",
    onClick: () => navigate("/documentation"),
  },
  {
    label: "Your Walkthroughs",
    url: "/walkthroughs",
    onClick: () => navigate("/walkthroughs"),
  },
  {
    label: "Your Notebook",
    url: "/my-notebook",
    onClick: () => navigate("/my-notebook"),
  },
  {
    label: "Your Workspace",
    url: "/workspaces",
    onClick: () => navigate("/workspaces"),
  },
  {
    label: "Account Settings",
    url: `/account/${uid}`,
    onClick: () => navigate(`/account/${uid}`),
  },
];

  // Use loggedInOptions if signed in, otherwise loggedOutOptions
  const menuOptions = uid ? loggedInOptions : loggedOutOptions;
  const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    const fetchWorkspaceDetails = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${customDomain}/`,
                {
                    headers: {
                        "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                        "Requesting-User-Uid": uid,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setWorkspaceDetails(data);
            } else {
                console.error("Failed to fetch workspace details");
                navigate("/workspaces");
            }
        } catch (error) {
            console.error("Error fetching workspace details:", error);
            navigate("/workspaces");
        }
    };

    const handleArrowClick = () => {
        setIsTransitioning(true);
        // Small delay to allow transition to start
        setTimeout(() => {
            setShowUpload(true);
        }, 150);
    };

    const handleBackFromUpload = () => {
        setShowUpload(false);
        setIsTransitioning(false);
    };

    if (!workspaceDetails) {
        return (
            <div className="DocumentationHomeComponent__loading-animation-div">
                <i className="fas fa-spinner fa-spin DocumentationHomeComponent__loading-animation-icon"></i>
            </div>
        );
    }

if (showUpload) {
    return (
        <UploadWorkspaceDocument
            onBack={handleBackFromUpload}
            workspaceId={workspaceDetails.id}
            enhancedRightClickEnabled={enhancedRightClickEnabled}
            showContextMenu={showContextMenu}
            ContextMenuComponent={ContextMenuComponent}
        />
    );
}

    return (
        <div 
            className={`ImportDocument__main-div ${isTransitioning ? 'transitioning' : ''}`}
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            <div className="ImportDocument__content">
                <h2>Let's Import Your Document to "{workspaceDetails.name}" Workspace...</h2>

                <p className="medium" style={{ marginTop: "20px" }}>
                    Thank you for choosing Bluebird Documentation to host your team workspace documents! This process 
                    is to import a single document from another documentation platform into your team workspace.
                </p>

                <p className="small">
                    We'll try to make instructions as clear as possible. If you're facing issues, need further guidance, 
                    or have encountered a bug, please feel free to submit a support ticket.
                </p>

                <p className="medium semi-bold">
                    Below is a general overview of the process.
                </p>

                <ol>
                    <li>Click the arrow on the right of the page to begin.</li>
                    <li>Select the HTML or Markdown (.md) file you have exported from your other platform.</li>
                    <li>After we've parsed the document, you'll get the chance to review it and make any necessary changes.</li>
                    <li>Choose whether to create this as a Space or Page in your workspace.</li>
                    <li>Lastly, set document permissions and visibility settings.</li>
                </ol>

                <p className="small">
                    This is mostly a work in progress, and there are likely to be issues. We really appreciate you're trying 
                    this feature, and please feel free to submit feedback, so we can continue to improve this feature!
                </p>

            </div>

            <div 
                className="ImportDocument__arrow" 
                onClick={handleArrowClick}
                title="Click to continue"
                aria-label="Continue to next step"
            >
                ❯
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    )
}

export default ImportWorkspaceDocument
